// Types de visuels du contrat C0 (§2) : liste, libellés français, silhouettes de skeleton.
// Module minuscule, chargé dans le bundle initial (skeleton dès `artifact_pending`) ;
// la lecture complète des spécifications (visualSpec.js) est dans le chunk des visuels.

export const VISUAL_TYPES = [
  'line', 'area', 'bar', 'stacked_bar', 'horizontal_bar', 'grouped_horizontal_bar',
  'pie', 'donut', 'gauge', 'kpi_cards', 'reliability_cards', 'ranking',
  'heatmap', 'scatter', 'waterfall', 'table',
];

// Libellé français de chaque type (badge de la carte, sélecteur de type)
export const TYPE_LABELS = {
  line: 'Courbe',
  area: 'Aire',
  bar: 'Barres',
  stacked_bar: 'Barres empilées',
  horizontal_bar: 'Barres horizontales',
  grouped_horizontal_bar: 'Barres groupées',
  pie: 'Camembert',
  donut: 'Anneau',
  gauge: 'Jauge',
  kpi_cards: 'Indicateurs',
  reliability_cards: 'Fiabilité des données',
  ranking: 'Classement',
  heatmap: 'Carte de chaleur',
  scatter: 'Nuage de points',
  waterfall: 'Cascade',
  table: 'Tableau',
  // Sprint 4 (contrat v1.2) : cartes, planning, intégrations web
  markers: 'Carte',
  heat: 'Carte de densité',
  route: 'Itinéraire',
  // Contrat v1.10 : trajet réellement parcouru (positions GPS lues par le serveur)
  trace: 'Trace GPS',
  zones: 'Carte des zones',
  planning: 'Planning',
  embed: 'Page web',
  // Contrat v1.13 : galerie de photos d'un véhicule (construite par le serveur)
  photos: 'Photos',
};

// Familles du sprint 4 (contrat v1.2) : rendues hors ECharts, chacune dans son chunk
export const MAP_TYPES = ['markers', 'heat', 'route', 'trace', 'zones'];

/**
 * Famille de rendu d'un visuel : 'map' (MapLibre), 'planning' (grille horaire),
 * 'embed' (iframe ou carte de lien), 'document' (fichier généré, sprint 5), 'email'
 * (brouillon d'e-mail), 'email_cancel' (carte d'annulation d'un envoi programmé), 'agent' et
 * 'agent_change' (cartes « Nouvel agent » et « Modification d'agent », contrat v1.15), 'photos'
 * (galerie de photos d'un véhicule, contrat v1.13) ou 'chart' (graphiques, tuiles, tableaux).
 * `visual_type` prime sur `kind` (le premier `artifact_pending` n'a pas encore de type).
 */
export function visualFamily(kind, visualType) {
  // Sprint 5 (contrat v1.3) : document généré (PDF, Word, Excel, PowerPoint)
  if (kind === 'document') return 'document';
  // Brouillon d'e-mail préparé par l'assistant, envoyé par l'utilisateur ; carte d'annulation
  // d'un envoi programmé, annulé par le clic de l'utilisateur
  if (kind === 'email' || kind === 'email_cancel') return kind;
  // Agents (P1) : la carte prépare la création ou la modification, seul le clic de
  // l'utilisateur l'applique (sans ce test, un genre inconnu retomberait en graphique)
  if (kind === 'agent' || kind === 'agent_change') return kind;
  if (kind === 'photos' || visualType === 'photos') return 'photos';
  if (MAP_TYPES.includes(visualType)) return 'map';
  if (visualType === 'planning') return 'planning';
  if (visualType === 'embed') return 'embed';
  if (visualType === 'map') return 'map';
  // Type de graphique connu : il prime ; sinon la famille annoncée par `kind`
  if (VISUAL_TYPES.includes(visualType)) return 'chart';
  if (['map', 'planning', 'embed'].includes(kind)) return kind;
  return 'chart';
}

// Types rendus en React/CSS (pas d'ECharts : rien de lourd à charger)
export const HTML_TYPES = new Set(['kpi_cards', 'reliability_cards', 'table']);

/** Silhouette du skeleton pour un type annoncé par `artifact_pending`. */
export function skeletonShape(visualType, kind) {
  switch (visualType) {
    case 'line':
    case 'area':
    case 'scatter':
      return 'line';
    case 'bar':
    case 'stacked_bar':
    case 'waterfall':
      return 'bars';
    case 'horizontal_bar':
    case 'grouped_horizontal_bar':
    case 'ranking':
      return 'hbars';
    case 'pie':
    case 'donut':
      return 'donut';
    case 'gauge':
      return 'gauge';
    case 'kpi_cards':
    case 'reliability_cards':
      return 'tiles';
    case 'table':
    case 'heatmap':
      return 'table';
    case 'markers':
    case 'heat':
    case 'zones':
      return 'map';
    case 'route':
    case 'trace':
      return 'route';
    case 'planning':
      return 'planning';
    case 'embed':
      return 'embed';
    default:
      if (kind === 'map') return 'map';
      if (kind === 'planning') return 'planning';
      if (kind === 'embed') return 'embed';
      return kind === 'table' ? 'table' : kind === 'kpi' ? 'tiles' : 'generic';
  }
}
